import React, { useState, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import IndividualViewerPanel, { IndividualViewerPanelRef } from './IndividualViewerPanel'
import ABTStyleControls from './ABTStyleControls'

interface MultiViewPanelProps {
  currentImageId: string
  caseId?: string
  onClose: () => void
}

interface SlideInfo {
  id: string
  name: string
  case: string
}

interface ImageAdjustments {
  brightness: number
  contrast: number
  saturation: number
  gamma: number
  redChannel: number
  greenChannel: number
  blueChannel: number
}

const MultiViewPanel: React.FC<MultiViewPanelProps> = ({
  currentImageId,
  caseId,
  onClose
}) => {
  const [availableImages, setAvailableImages] = useState<SlideInfo[]>([])
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>([currentImageId])
  const [loading, setLoading] = useState(true)
  const [activeImageId, setActiveImageId] = useState<string>(currentImageId)
  const [activeViewer, setActiveViewer] = useState<any>(null)

  // Debug: Log when activeImageId changes
  useEffect(() => {
    console.log('[MultiViewPanel] Active image changed to:', activeImageId)
  }, [activeImageId])

  // Refs for each viewer panel
  const viewerPanelRefs = useRef<{ [key: string]: IndividualViewerPanelRef | null }>({})

  // Individual adjustments for each image (independent)
  // Use ref to persist adjustments across re-renders
  const imageAdjustmentsRef = useRef<{ [key: string]: ImageAdjustments }>({})
  const [imageAdjustments, setImageAdjustments] = useState<{ [key: string]: ImageAdjustments }>({})

  // Current adjustment values from ABTStyleControls (synced with active image)
  const [currentAdjustments, setCurrentAdjustments] = useState<ImageAdjustments>({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    gamma: 1,
    redChannel: 100,
    greenChannel: 100,
    blueChannel: 100
  })

  // Sync Lock: Support multiple sync groups
  // Each image can belong to one sync group (identified by group ID)
  // imageId -> groupId mapping
  const [imageSyncGroups, setImageSyncGroups] = useState<{ [imageId: string]: string }>({})
  const [isSyncEnabled, setIsSyncEnabled] = useState(false)

  // Toggle left panel visibility
  const [showLeftPanel, setShowLeftPanel] = useState(true)

  // Group colors for visual distinction
  const syncGroupColors = ['blue', 'green', 'purple', 'pink', 'indigo', 'red']

  // Track images currently being synced to prevent infinite loops
  const syncingImagesRef = useRef<Set<string>>(new Set())

  // ABTStyleControls states (required props)
  const [annotationTool, setAnnotationTool] = useState<string | null>(null)
  const [hiddenAnnotations, setHiddenAnnotations] = useState<Set<string>>(new Set())
  const [showAIPanel, setShowAIPanel] = useState(false)
  const [showAnnotationTool, setShowAnnotationTool] = useState(false)
  const [showReportPanel, setShowReportPanel] = useState(false)
  const [showSnapshotPanel, setShowSnapshotPanel] = useState(false)

  // Fetch available images for the same patient/case
  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true)

        // First, get current slide info to find case_id
        const slideResponse = await fetch(`/api/v1/imaging/slides/${currentImageId}/`)
        if (!slideResponse.ok) {
          throw new Error('Failed to fetch slide info')
        }

        const slideData = await slideResponse.json()
        const patientCaseId = caseId || slideData.case

        if (!patientCaseId) {
          toast.error('No case found for this slide')
          setAvailableImages([slideData])
          setLoading(false)
          return
        }

        // Get case info to find patient ID
        const caseResponse = await fetch(`/api/v1/pathology/cases/${patientCaseId}/`)
        if (!caseResponse.ok) {
          throw new Error('Failed to fetch case info')
        }

        const caseData = await caseResponse.json()
        const patientId = caseData.patient?.id

        if (!patientId) {
          // No patient, fall back to case-only view
          const imagesResponse = await fetch(`/api/v1/imaging/slides/?case=${patientCaseId}`)
          if (!imagesResponse.ok) {
            throw new Error('Failed to fetch case images')
          }
          const imagesData = await imagesResponse.json()
          setAvailableImages(imagesData.results || [])
          setLoading(false)
          return
        }

        // Get all cases for this patient (handle pagination)
        const allCases: any[] = []
        let nextUrl: string | null = `/api/v1/pathology/cases/?patient=${patientId}`

        while (nextUrl) {
          const casesResponse: Response = await fetch(nextUrl)
          if (!casesResponse.ok) {
            throw new Error('Failed to fetch patient cases')
          }

          const casesData: any = await casesResponse.json()
          allCases.push(...(casesData.results || []))
          // Fix mixed content - replace http:// with https://
          nextUrl = casesData.next ? casesData.next.replace('http://', 'https://') : null
        }

        const cases = allCases

        // Get all slides from all cases for this patient
        const allSlides: SlideInfo[] = []
        for (const patientCase of cases) {
          const slidesResponse = await fetch(`/api/v1/imaging/slides/?case=${patientCase.id}`)
          if (slidesResponse.ok) {
            const slidesData = await slidesResponse.json()
            const slides = slidesData.results || []
            allSlides.push(...slides)
          }
        }

        setAvailableImages(allSlides)

      } catch (error) {
        console.error('Error fetching images:', error)
        toast.error('Failed to load patient images')
      } finally {
        setLoading(false)
      }
    }

    fetchImages()
  }, [currentImageId, caseId])

  // Update active viewer when active image changes
  useEffect(() => {
    if (activeImageId && viewerPanelRefs.current[activeImageId]) {
      const viewer = viewerPanelRefs.current[activeImageId]?.getViewer()
      setActiveViewer(viewer)
    }
  }, [activeImageId])

  // Initialize default adjustments for new images
  useEffect(() => {
    const defaultAdjustments: ImageAdjustments = {
      brightness: 100,
      contrast: 100,
      saturation: 100,
      gamma: 1,
      redChannel: 100,
      greenChannel: 100,
      blueChannel: 100
    }

    setImageAdjustments(prev => {
      const updated = { ...prev }
      let hasChanges = false

      selectedImageIds.forEach(imageId => {
        if (!updated[imageId]) {
          updated[imageId] = { ...defaultAdjustments }
          hasChanges = true
        }
      })

      return hasChanges ? updated : prev
    })
  }, [selectedImageIds])

  // Load active image's adjustments into current adjustments (only when activeImageId changes)
  useEffect(() => {
    if (!activeImageId) return

    // Get adjustments for active image from ref (or defaults)
    const adjustments = imageAdjustmentsRef.current[activeImageId] || {
      brightness: 100,
      contrast: 100,
      saturation: 100,
      gamma: 1,
      redChannel: 100,
      greenChannel: 100,
      blueChannel: 100
    }

    console.log('[MultiView] Loading adjustments for', activeImageId, adjustments)
    console.log('[MultiView] All stored adjustments in ref:', imageAdjustmentsRef.current)
    setCurrentAdjustments({ ...adjustments })
  }, [activeImageId]) // Only depend on activeImageId to avoid loops

  // Handle adjustment changes from ABTStyleControls
  const handleAdjustmentChange = (newAdjustments: Partial<ImageAdjustments>) => {
    if (!activeImageId) return

    const defaultAdjustments = {
      brightness: 100,
      contrast: 100,
      saturation: 100,
      gamma: 1,
      redChannel: 100,
      greenChannel: 100,
      blueChannel: 100
    }

    // Check if sync is enabled and active image belongs to a sync group
    const activeGroupId = imageSyncGroups[activeImageId]
    const isActiveImageSynced = isSyncEnabled && activeGroupId

    if (isActiveImageSynced) {
      // Apply adjustments to ALL images in the same group
      const updatedAdjustments = { ...imageAdjustmentsRef.current }
      let syncedCount = 0

      Object.entries(imageSyncGroups).forEach(([imageId, groupId]) => {
        if (groupId === activeGroupId) {
          const currentForImage = imageAdjustmentsRef.current[imageId] || defaultAdjustments
          updatedAdjustments[imageId] = { ...currentForImage, ...newAdjustments }
          syncedCount++
        }
      })

      imageAdjustmentsRef.current = updatedAdjustments
      console.log('[MultiView] Applied synced adjustments to', syncedCount, 'images in group', activeGroupId)
    } else {
      // Apply to active image only (original behavior)
      const currentForImage = imageAdjustmentsRef.current[activeImageId] || defaultAdjustments
      const updated = { ...currentForImage, ...newAdjustments }

      imageAdjustmentsRef.current = {
        ...imageAdjustmentsRef.current,
        [activeImageId]: updated
      }

      console.log('[MultiView] Saved adjustments for', activeImageId, updated)
    }

    // Update current adjustments (for display in ABTStyleControls)
    const updatedForActive = imageAdjustmentsRef.current[activeImageId]
    setCurrentAdjustments(updatedForActive)

    // Update state to trigger re-render of IndividualViewerPanel
    setImageAdjustments({ ...imageAdjustmentsRef.current })
  }

  // Handle viewer ready callback
  const handleViewerReady = (imageId: string, viewer: any) => {
    if (imageId === activeImageId) {
      setActiveViewer(viewer)
    }
  }

  const addImage = (imageId: string) => {
    if (selectedImageIds.includes(imageId)) {
      toast.error('Image already added')
      return
    }

    if (selectedImageIds.length >= 6) {
      toast.error('Maximum 6 images allowed')
      return
    }

    setSelectedImageIds([...selectedImageIds, imageId])
    toast.success('Image added to multi-view')
  }

  const removeImage = (imageId: string) => {
    if (selectedImageIds.length === 1) {
      toast.error('At least one image must be shown')
      return
    }

    setSelectedImageIds(selectedImageIds.filter(id => id !== imageId))
    toast.success('Image removed from multi-view')
  }

  // Zoom controls for active viewer
  const handleZoomPreset = (multiplier: number) => {
    if (!activeViewer) return

    const activeGroupId = imageSyncGroups[activeImageId]
    const isActiveImageSynced = isSyncEnabled && activeGroupId

    if (isActiveImageSynced) {
      // Apply zoom to ALL images in the same group
      let syncedCount = 0
      Object.entries(imageSyncGroups).forEach(([imageId, groupId]) => {
        if (groupId === activeGroupId) {
          const panel = viewerPanelRefs.current[imageId]
          if (panel) {
            const viewer = panel.getViewer()
            if (viewer) {
              const homeZoom = viewer.viewport.getHomeZoom()
              viewer.viewport.zoomTo(homeZoom * multiplier)
              syncedCount++
            }
          }
        }
      })
      console.log('[MultiView] Applied synced zoom to', syncedCount, 'images in group', activeGroupId)
    } else {
      // Apply to active viewer only
      const homeZoom = activeViewer.viewport.getHomeZoom()
      activeViewer.viewport.zoomTo(homeZoom * multiplier)
    }
  }

  const handleZoomFit = () => {
    if (!activeViewer) return

    const activeGroupId = imageSyncGroups[activeImageId]
    const isActiveImageSynced = isSyncEnabled && activeGroupId

    if (isActiveImageSynced) {
      // Apply zoom fit to ALL images in the same group
      let syncedCount = 0
      Object.entries(imageSyncGroups).forEach(([imageId, groupId]) => {
        if (groupId === activeGroupId) {
          const panel = viewerPanelRefs.current[imageId]
          if (panel) {
            const viewer = panel.getViewer()
            if (viewer) {
              viewer.viewport.goHome()
              syncedCount++
            }
          }
        }
      })
      console.log('[MultiView] Applied synced zoom fit to', syncedCount, 'images in group', activeGroupId)
    } else {
      // Apply to active viewer only
      activeViewer.viewport.goHome()
    }
  }

  // Handle zoom change from mouse wheel (for sync lock)
  const handleZoomChange = (sourceImageId: string, zoomRatio: number) => {
    console.log('[MultiView] handleZoomChange called:', { sourceImageId, zoomRatio, isSyncEnabled })

    // PREVENT INFINITE LOOP: Skip if this image is currently being synced
    if (syncingImagesRef.current.has(sourceImageId)) {
      console.log('[MultiView] Skipping zoom sync - image is being synced programmatically')
      return
    }

    if (!isSyncEnabled) {
      console.log('[MultiView] Sync not enabled, skipping zoom sync')
      return
    }

    const sourceGroupId = imageSyncGroups[sourceImageId]
    console.log('[MultiView] Source group ID:', sourceGroupId)
    console.log('[MultiView] All sync groups:', imageSyncGroups)

    if (!sourceGroupId) {
      console.log('[MultiView] Source image not in any group, skipping zoom sync')
      return
    }

    // Sync zoom to all other images in the same group
    let syncedCount = 0
    Object.entries(imageSyncGroups).forEach(([imageId, groupId]) => {
      if (groupId === sourceGroupId && imageId !== sourceImageId) {
        console.log('[MultiView] Syncing zoom to imageId:', imageId)

        // Mark this image as being synced to prevent infinite loop
        syncingImagesRef.current.add(imageId)

        const panel = viewerPanelRefs.current[imageId]
        if (panel) {
          const viewer = panel.getViewer()
          if (viewer) {
            const homeZoom = viewer.viewport.getHomeZoom()
            console.log('[MultiView] Applying zoom:', { imageId, homeZoom, zoomRatio, targetZoom: homeZoom * zoomRatio })
            viewer.viewport.zoomTo(homeZoom * zoomRatio, null, true) // immediately: true for smooth sync
            syncedCount++
          }
        }

        // Clear the syncing flag after a short delay (animation complete)
        setTimeout(() => {
          syncingImagesRef.current.delete(imageId)
          console.log('[MultiView] Cleared syncing flag for imageId:', imageId)
        }, 100)
      }
    })
    console.log('[MultiView] Mouse wheel zoom synced to', syncedCount, 'images in group', sourceGroupId)
  }

  // Handle viewport change (pan + zoom) for sync lock
  const handleViewportChange = (sourceImageId: string, center: { x: number, y: number }, zoom: number) => {
    // PREVENT INFINITE LOOP: Skip if this image is currently being synced
    if (syncingImagesRef.current.has(sourceImageId)) {
      return
    }

    if (!isSyncEnabled) return

    const sourceGroupId = imageSyncGroups[sourceImageId]
    if (!sourceGroupId) return

    // Sync viewport to all other images in the same group
    Object.entries(imageSyncGroups).forEach(([imageId, groupId]) => {
      if (groupId === sourceGroupId && imageId !== sourceImageId) {
        // Mark this image as being synced to prevent infinite loop
        syncingImagesRef.current.add(imageId)

        const panel = viewerPanelRefs.current[imageId]
        if (panel) {
          const viewer = panel.getViewer()
          if (viewer && viewer.viewport) {
            const homeZoom = viewer.viewport.getHomeZoom()
            // Pan to center position
            viewer.viewport.panTo({ x: center.x, y: center.y }, true) // immediately: true
            viewer.viewport.zoomTo(homeZoom * zoom, null, true)
          }
        }

        // Clear the syncing flag after a short delay (animation complete)
        setTimeout(() => {
          syncingImagesRef.current.delete(imageId)
        }, 100)
      }
    })
  }

  // Calculate grid layout based on number of images
  const getGridClass = () => {
    const count = selectedImageIds.length
    if (count === 1) return 'grid-cols-1'
    if (count === 2) return 'grid-cols-2'
    if (count <= 4) return 'grid-cols-2'
    if (count <= 6) return 'grid-cols-3'
    return 'grid-cols-3'
  }

  const getGridRows = () => {
    const count = selectedImageIds.length
    if (count <= 2) return 'grid-rows-1'
    if (count <= 4) return 'grid-rows-2'
    return 'grid-rows-2'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex">
      {/* Left Panel - Image Selection */}
      {showLeftPanel && (
        <div className="w-56 bg-white h-full overflow-y-auto flex flex-col">
        <div className="p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-800">Multi-View</h2>
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-gray-800"
              title="Close multi-view"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            {selectedImageIds.length} / 6 images
          </p>

          {/* Sync Lock Controls */}
          <div className="space-y-2">
            <button
              onClick={() => setIsSyncEnabled(!isSyncEnabled)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm font-medium transition-colors ${
                isSyncEnabled
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isSyncEnabled ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  )}
                </svg>
                {isSyncEnabled ? 'Sync Enabled' : 'Sync Disabled'}
              </span>
              <span className="text-xs">
                {Object.keys(imageSyncGroups).length} locked
              </span>
            </button>

            {isSyncEnabled && (
              <div className="space-y-2">
                <button
                  onClick={() => {
                    // Create new group with selected images that don't have a group yet
                    const newGroupId = `group-${Date.now()}`
                    const updates: { [key: string]: string } = {}
                    selectedImageIds.forEach(id => {
                      if (!imageSyncGroups[id]) {
                        updates[id] = newGroupId
                      }
                    })
                    if (Object.keys(updates).length > 0) {
                      setImageSyncGroups({ ...imageSyncGroups, ...updates })
                      toast.success(`Created group with ${Object.keys(updates).length} images`)
                    } else {
                      toast('All images already in groups')
                    }
                  }}
                  className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Create New Group
                </button>
                <button
                  onClick={() => {
                    setImageSyncGroups({})
                    toast.success('Unlocked all groups')
                  }}
                  className="w-full px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Unlock All
                </button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-gray-600">Loading images...</div>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {availableImages.map((image) => {
              const isSelected = selectedImageIds.includes(image.id)
              const isCurrent = image.id === currentImageId
              const groupId = imageSyncGroups[image.id]
              const isLocked = !!groupId

              // Get group color index for this image's group
              const groupIds = Array.from(new Set(Object.values(imageSyncGroups)))
              const groupIndex = groupId ? groupIds.indexOf(groupId) : -1
              const groupColor = groupIndex >= 0 ? syncGroupColors[groupIndex % syncGroupColors.length] : 'blue'

              return (
                <div
                  key={image.id}
                  className={`p-3 rounded border-2 transition-all ${
                    isSelected
                      ? isLocked && isSyncEnabled
                        ? `border-${groupColor}-500 bg-${groupColor}-50`
                        : 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {/* Sync Group Toggle Button */}
                    {isSelected && isSyncEnabled && (
                      <button
                        onClick={() => {
                          if (groupId) {
                            // Remove from current group
                            const updated = { ...imageSyncGroups }
                            delete updated[image.id]
                            setImageSyncGroups(updated)
                            toast.success('Removed from group')
                          } else {
                            // Find the active image's group, or create new group
                            const activeGroupId = imageSyncGroups[activeImageId]
                            if (activeGroupId) {
                              // Add to active image's group
                              setImageSyncGroups({ ...imageSyncGroups, [image.id]: activeGroupId })
                              toast.success('Added to active group')
                            } else {
                              // Create new group with this image and active image
                              const newGroupId = `group-${Date.now()}`
                              setImageSyncGroups({
                                ...imageSyncGroups,
                                [image.id]: newGroupId,
                                [activeImageId]: newGroupId
                              })
                              toast.success('Created new group')
                            }
                          }
                        }}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isLocked
                            ? `border-${groupColor}-600 bg-${groupColor}-100 text-${groupColor}-600`
                            : 'border-gray-400 bg-white hover:border-blue-500'
                        }`}
                        title={isLocked ? 'Remove from group' : 'Add to group'}
                      >
                        {isLocked && <span className="text-xs">✓</span>}
                      </button>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-800 truncate">
                        {image.name || `Slide ${image.id.slice(0, 8)}`}
                      </div>
                      {isCurrent && (
                        <div className="text-xs text-blue-600 font-semibold mt-1">
                          Current
                        </div>
                      )}
                      {isLocked && isSyncEnabled && (
                        <div className={`text-xs text-${groupColor}-600 font-semibold mt-1`}>
                          🔒 Group {groupIndex + 1}
                        </div>
                      )}
                    </div>
                    {isSelected ? (
                      <button
                        onClick={() => removeImage(image.id)}
                        className="ml-2 px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                        disabled={selectedImageIds.length === 1}
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        onClick={() => addImage(image.id)}
                        className="ml-2 px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
                        disabled={selectedImageIds.length >= 6}
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              )
            })}

            {availableImages.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                No other images found for this patient
              </div>
            )}
          </div>
        )}
        </div>
      )}

      {/* Toggle Button for Left Panel - Above Zoom Menu */}
      <button
        onClick={() => setShowLeftPanel(!showLeftPanel)}
        className="fixed z-50 bg-blue-500 hover:bg-blue-600 text-white rounded shadow-lg p-2 transition-all"
        style={{
          top: '20px',
          left: showLeftPanel ? '240px' : '16px'
        }}
        title={showLeftPanel ? 'Hide menu' : 'Show menu'}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {showLeftPanel ? (
            // Chevron left icon (hide)
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          ) : (
            // Chevron right icon (show)
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          )}
        </svg>
      </button>

      {/* Zoom Menu - Fixed Left (outside grid area) */}
      {activeViewer && (
        <div
          className="fixed z-40 flex flex-col gap-2 transition-all"
          style={{
            top: '50%',
            transform: 'translateY(-50%)',
            left: showLeftPanel ? '240px' : '16px', // Adjust based on left panel visibility
            opacity: 0.9
          }}
        >
          {/* Zoom Indicator */}
          <div className="px-2 py-1 bg-white border border-blue-500 rounded text-blue-600 font-bold text-xs min-w-[40px] text-center shadow-lg">
            {activeViewer ? `${(activeViewer.viewport.getZoom() / activeViewer.viewport.getHomeZoom()).toFixed(1)}x` : '1.0x'}
          </div>

          {/* Zoom Levels - from top to bottom: 100X, 80X, ... Fit */}
          <button onClick={() => handleZoomPreset(100)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">100X</button>
          <button onClick={() => handleZoomPreset(80)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">80X</button>
          <button onClick={() => handleZoomPreset(60)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">60X</button>
          <button onClick={() => handleZoomPreset(40)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">40X</button>
          <button onClick={() => handleZoomPreset(20)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">20X</button>
          <button onClick={() => handleZoomPreset(10)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">10X</button>
          <button onClick={() => handleZoomPreset(4)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">4X</button>
          <button onClick={handleZoomFit} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">Fit</button>
        </div>
      )}

      {/* Middle Panel - Multi-View Grid (smaller area) */}
      <div className="flex-1 p-4 relative transition-all" style={{ marginLeft: showLeftPanel ? '48px' : '4px', marginRight: '80px' }}>
        <div className={`grid ${getGridClass()} ${getGridRows()} gap-4 h-full`}>
          {selectedImageIds.map((imageId) => {
            const image = availableImages.find(img => img.id === imageId)

            return (
              <IndividualViewerPanel
                key={imageId}
                ref={(el) => {
                  viewerPanelRefs.current[imageId] = el
                }}
                imageId={imageId}
                imageName={image?.name || `Slide ${imageId.slice(0, 8)}`}
                isSelected={activeImageId === imageId}
                onSelect={() => {
                  console.log('[MultiViewPanel] onSelect called for imageId:', imageId)
                  console.log('[MultiViewPanel] Current activeImageId:', activeImageId)
                  console.log('[MultiViewPanel] Comparison result (isSelected):', activeImageId === imageId)
                  setActiveImageId(imageId)
                }}
                onRemove={selectedImageIds.length > 1 ? () => removeImage(imageId) : undefined}
                showRemoveButton={selectedImageIds.length > 1}
                onViewerReady={(viewer) => handleViewerReady(imageId, viewer)}
                onZoomChange={handleZoomChange}
                onViewportChange={handleViewportChange}
                adjustments={imageAdjustments[imageId]}
              />
            )
          })}
        </div>
      </div>

      {/* ABTStyleControls - Fixed Right (outside grid area) */}
      {activeViewer && activeImageId && (
        <ABTStyleControls
          viewer={activeViewer}
          onAnnotationToolChange={setAnnotationTool}
          selectedAnnotationTool={annotationTool}
          onClearAnnotations={() => {}}
          onSaveAnnotations={() => {}}
          hasUnsavedAnnotations={false}
          currentImageId={activeImageId}
          hiddenAnnotations={hiddenAnnotations}
          onToggleAnnotationVisibility={(id) => {
            setHiddenAnnotations(prev => {
              const newSet = new Set(prev)
              if (newSet.has(id)) {
                newSet.delete(id)
              } else {
                newSet.add(id)
              }
              return newSet
            })
          }}
          onToggleAIPanel={() => setShowAIPanel(!showAIPanel)}
          showAIPanel={showAIPanel}
          onToggleAnnotationTool={() => setShowAnnotationTool(!showAnnotationTool)}
          showAnnotationTool={showAnnotationTool}
          onToggleReportPanel={() => setShowReportPanel(!showReportPanel)}
          showReportPanel={showReportPanel}
          onToggleSnapshotPanel={() => setShowSnapshotPanel(!showSnapshotPanel)}
          showSnapshotPanel={showSnapshotPanel}
          onToggleMultiView={onClose}
          showMultiView={true}
          controlledAdjustments={currentAdjustments}
          onAdjustmentChange={handleAdjustmentChange}
        />
      )}
    </div>
  )
}

export default MultiViewPanel
